/**
 * The browser's only door to `/api/v1`.
 *
 * Every call is cookie-authenticated (`credentials: "include"`), every non-GET
 * carries the CSRF token the session handed us, and every non-2xx response is
 * normalised into a single {@link ApiError} — so callers never branch on
 * `response.ok`, HTTP status codes, or the shape of an error envelope.
 *
 * The CSRF token lives in a module variable rather than a React context because
 * this module must stay importable from loaders, tests and non-component code.
 * `SessionProvider` pushes the token in through {@link setCsrfToken} whenever
 * `/me` resolves; nothing else may write it.
 */
import { ApiErrorSchema, type ErrorCode } from "@hrms/shared";
import type { ZodType } from "zod";

export const API_BASE = "/api/v1";

/* ------------------------------ CSRF token ------------------------------- */

let csrfToken: string | null = null;

/** Called by SessionProvider on every successful `/me` load (and on sign-out). */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/* -------------------------------- errors --------------------------------- */

export type FieldErrors = Record<string, string[]>;

/**
 * Field keys arrive in whatever shape the route's zod schema happens to use: camelCase
 * from the object schemas (`personalEmail`), a dotted zod path from a nested one
 * (`entries.0.amount`), or the `_` the settings routes use for a whole-object
 * refinement. Forms address their fields by whichever name *they* hold — MyProfile
 * asks for `personal_email` because that is the wire name in `SENSITIVE_FIELDS` — and a
 * lookup that misses is silent twice over: no inline message, and no form-level banner
 * either, because that fallback is gated on `fieldErrors` being empty and it is not.
 *
 * So keys are indexed by a punctuation- and case-free form, and lookups are folded the
 * same way. `personalEmail`, `personal_email` and `PersonalEmail` are one key.
 */
const foldFieldKey = (name: string): string => name.replace(/[^a-z0-9]/gi, "").toLowerCase();

/**
 * `entries.0.amount` is also indexed under its leaf, `amount`, so a form that names its
 * control after the field rather than the path still finds the message. A leaf shared by
 * two different paths is ambiguous and is dropped rather than attributed to the wrong
 * control.
 */
function indexFieldErrors(fieldErrors: FieldErrors): Map<string, string[]> {
  const index = new Map<string, string[]>();
  const leaves = new Map<string, string>();

  for (const [key, messages] of Object.entries(fieldErrors)) {
    if (messages.length === 0) continue;
    index.set(foldFieldKey(key), messages);
  }
  for (const [key, messages] of Object.entries(fieldErrors)) {
    if (messages.length === 0 || !key.includes(".")) continue;
    const leaf = foldFieldKey(key.slice(key.lastIndexOf(".") + 1));
    if (!leaf || index.has(leaf)) continue;
    const claimant = leaves.get(leaf);
    if (claimant && claimant !== key) {
      index.delete(leaf);
      continue;
    }
    leaves.set(leaf, key);
    index.set(leaf, messages);
  }
  return index;
}

/** `personalEmail` → "Personal email", for a banner that has no label to borrow. */
function humaniseFieldKey(key: string): string {
  const leaf = key.includes(".") ? key.slice(key.lastIndexOf(".") + 1) : key;
  const words = leaf
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : leaf;
}

/**
 * Every failure the UI can render: a server error envelope, a transport failure,
 * or a response that did not match its schema. `code` is the discriminator the UI
 * switches on; `fieldErrors` feeds inline form messages.
 */
export class ApiError extends Error {
  override readonly name = "ApiError";
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId: string;
  /** Exactly what the server sent, untouched — some callers copy it into state. */
  readonly fieldErrors: FieldErrors;

  /** Folded key → messages, so a lookup never depends on the caller's casing. */
  private readonly index: Map<string, string[]>;
  /** Folded keys some control has already rendered inline. Feeds {@link formError}. */
  private readonly claimed = new Set<string>();

  constructor(init: {
    code: ErrorCode;
    message: string;
    status: number;
    requestId?: string;
    fieldErrors?: FieldErrors;
  }) {
    super(init.message);
    this.code = init.code;
    this.status = init.status;
    this.requestId = init.requestId ?? "";
    this.fieldErrors = init.fieldErrors ?? {};
    this.index = indexFieldErrors(this.fieldErrors);
  }

  /** True when the server blamed at least one field rather than the request as a whole. */
  get hasFieldErrors(): boolean {
    return this.index.size > 0;
  }

  /**
   * Inline message for one form field, if the server flagged it — under any casing.
   * Asking counts as rendering it: the field is then excluded from {@link formError},
   * so the same message is never shown twice.
   */
  fieldError(field: string): string | undefined {
    return this.fieldMessages(field)[0];
  }

  /** Every message for one field, for the rare control that shows more than one. */
  fieldMessages(field: string): string[] {
    const folded = foldFieldKey(field);
    const messages = this.index.get(folded);
    if (messages) this.claimed.add(folded);
    return messages ?? [];
  }

  /**
   * The sentence a form shows above its buttons when it cannot say everything inline.
   *
   * Undefined only when every field error has already been claimed by a control, so a
   * failure can never be swallowed: a 422 with no field errors returns the envelope
   * message, and a 422 whose fields no control asked for returns the message *plus*
   * those orphaned complaints. Read it after rendering the fields — asking a `Field`
   * for its error is what marks the message as spoken for.
   */
  get formError(): string | undefined {
    const orphans: string[] = [];
    for (const [key, messages] of Object.entries(this.fieldErrors)) {
      if (messages.length === 0) continue;
      const folded = foldFieldKey(key);
      if (this.claimed.has(folded)) continue;
      // `_` is the settings routes' whole-object refinement — it has no field to name.
      const label = key === "_" ? "" : `${humaniseFieldKey(key)}: `;
      orphans.push(`${label}${messages[0]}`);
    }
    if (orphans.length === 0) return this.hasFieldErrors ? undefined : this.message;
    return `${this.message} ${orphans.join(" ")}`;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** True when re-authenticating is the only way forward. */
export function isAuthError(error: unknown): boolean {
  return isApiError(error) && (error.code === "unauthenticated" || error.code === "forbidden");
}

/* ------------------------------- requests -------------------------------- */

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions<T> {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  /** JSON body. Ignored when `formData` is present. */
  body?: unknown;
  /** Multipart body for document/certificate uploads. */
  formData?: FormData;
  query?: Record<string, QueryValue | readonly QueryValue[]>;
  /** Parsed with this schema when supplied, so a contract drift fails loudly here. */
  schema?: ZodType<T>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: RequestOptions<unknown>["query"]): string {
  const url = path.startsWith("/api") ? path : `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null && item !== "") params.append(key, String(item));
      }
    } else {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

const FALLBACK_MESSAGES: Partial<Record<ErrorCode, string>> = {
  unauthenticated: "Your session has expired. Sign in again to continue.",
  forbidden: "You don't have access to this.",
  not_found: "That record no longer exists.",
  conflict: "Someone changed this while you were working on it.",
  state_conflict: "That action is no longer available for this record.",
  rate_limited: "Too many attempts. Wait a moment and try again.",
  storage_unavailable: "File storage is unavailable right now.",
  database_unavailable: "The service is temporarily unavailable.",
  payload_too_large: "That file is too large.",
  unsupported_media_type: "That file type isn't accepted.",
  internal_error: "Something went wrong on our side.",
};

function statusToCode(status: number): ErrorCode {
  switch (status) {
    case 401: return "unauthenticated";
    case 403: return "forbidden";
    case 404: return "not_found";
    case 409: return "conflict";
    case 413: return "payload_too_large";
    case 415: return "unsupported_media_type";
    case 422: return "validation_failed";
    case 429: return "rate_limited";
    case 503: return "database_unavailable";
    default: return "internal_error";
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }
  const parsed = ApiErrorSchema.safeParse(payload);
  if (parsed.success) {
    const { code, message, requestId, fieldErrors } = parsed.data.error;
    return new ApiError({ code, message, status: response.status, requestId, fieldErrors });
  }
  const code = statusToCode(response.status);
  return new ApiError({
    code,
    message: FALLBACK_MESSAGES[code] ?? `Request failed (${response.status}).`,
    status: response.status,
  });
}

export async function request<T = unknown>(path: string, options: RequestOptions<T> = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json", ...options.headers };

  let body: BodyInit | undefined;
  if (options.formData) {
    body = options.formData; // fetch sets the multipart boundary itself.
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  // A GET can never mutate, so it never needs — and never leaks — the token.
  if (method !== "GET" && csrfToken) headers["x-csrf-token"] = csrfToken;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body,
      credentials: "include",
      signal: options.signal,
    });
  } catch (cause) {
    // An abort is the caller changing its mind, not the network failing, so it
    // has to propagate untouched — React Query cancels a superseded query this
    // way and would otherwise surface it as an unreachable server.
    //
    // Ask the signal rather than the thrown value. Browsers reject with a
    // DOMException named AbortError, but msw's Node interceptor rejects with a
    // plain TypeError, so sniffing the error alone turns every cancelled query
    // in the test suite into a status-0 "Can't reach BotpressoHR" — and trips
    // the global ErrorBanner with it.
    if (options.signal?.aborted) throw options.signal.reason ?? cause;
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError({
      code: "internal_error",
      message: "Can't reach BotpressoHR. Check your connection and try again.",
      status: 0,
    });
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!text) return undefined as T;

  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new ApiError({
      code: "internal_error",
      message: "The server sent a response we couldn't read.",
      status: response.status,
    });
  }

  if (!options.schema) return data as T;
  const parsed = options.schema.safeParse(data);
  if (!parsed.success) {
    throw new ApiError({
      code: "internal_error",
      message: "The server sent data in an unexpected shape.",
      status: response.status,
    });
  }
  return parsed.data;
}

type BodylessOptions<T> = Omit<RequestOptions<T>, "method" | "body" | "formData">;
type BodyOptions<T> = Omit<RequestOptions<T>, "method">;

export const api = {
  get: <T = unknown>(path: string, options?: BodylessOptions<T>) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T = unknown>(path: string, body?: unknown, options?: BodyOptions<T>) =>
    request<T>(path, { ...options, method: "POST", body }),
  patch: <T = unknown>(path: string, body?: unknown, options?: BodyOptions<T>) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  put: <T = unknown>(path: string, body?: unknown, options?: BodyOptions<T>) =>
    request<T>(path, { ...options, method: "PUT", body }),
  delete: <T = unknown>(path: string, options?: BodylessOptions<T>) =>
    request<T>(path, { ...options, method: "DELETE" }),
  /** Multipart upload — documents, medical certificates, resource files. */
  upload: <T = unknown>(path: string, formData: FormData, options?: BodylessOptions<T>) =>
    request<T>(path, { ...options, method: "POST", formData }),
};

export type Api = typeof api;
